/**
 * User Notification Preferences Page
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
} from '@mui/material';
import {
  Email as EmailIcon,
  Sms as SmsIcon,
  Notifications as NotificationsIcon,
  Chat as SlackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { UserNotificationPreferences, AlertType } from '../../types/alerts';

const NotificationPreferences: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch current preferences
  const { data: preferences, isLoading } = useQuery<UserNotificationPreferences>({
    queryKey: ['notificationPreferences'],
    queryFn: async () => {
      const response = await api.getMyNotificationPreferences();
      return response.data;
    },
  });

  // Form state
  const [formData, setFormData] = useState<Partial<UserNotificationPreferences>>({
    email_enabled: true,
    sms_enabled: false,
    in_app_enabled: true,
    slack_enabled: false,
    quiet_hours_enabled: false,
    daily_digest_enabled: false,
  });

  // Update form data when preferences are loaded
  useEffect(() => {
    if (preferences) {
      setFormData(preferences);
    }
  }, [preferences]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) => api.updateMyNotificationPreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
      queryClient.invalidateQueries({ queryKey: ['alertSummary'] });
      setSuccessMessage('Preferences saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: (error: any) => {
      alert(`Error saving preferences: ${error.response?.data?.detail || error.message}`);
    },
  });

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  const handleAlertPreferenceChange = (alertType: string, field: 'enabled' | 'channels', value: any) => {
    setFormData((prev) => ({
      ...prev,
      alert_preferences: {
        ...prev.alert_preferences,
        [alertType]: {
          ...(prev.alert_preferences?.[alertType] || {}),
          [field]: value,
        },
      },
    }));
  };

  const getAlertTypeLabel = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const alertTypes = [
    AlertType.STOCK_BELOW_MIN,
    AlertType.STOCKOUT,
    AlertType.ORDER_PENDING,
    AlertType.FORECAST_ANOMALY,
    AlertType.EXPIRY_WARNING,
    AlertType.SLOW_MOVING,
    AlertType.EXCESS_STOCK,
  ];

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading preferences...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Notification Preferences</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Customize how and when you receive alert notifications
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/alerts')}>
          Back to Alerts
        </Button>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Notification Channels */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Notification Channels
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enable or disable notification channels
            </Typography>

            <Grid container spacing={2}>
              {/* In-App Notifications */}
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <NotificationsIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="subtitle1">In-App Notifications</Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.in_app_enabled}
                          onChange={(e) =>
                            setFormData({ ...formData, in_app_enabled: e.target.checked })
                          }
                        />
                      }
                      label="Enable in-app notifications"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Show notifications in the notification bell and alerts page
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Email Notifications */}
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="subtitle1">Email Notifications</Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.email_enabled}
                          onChange={(e) =>
                            setFormData({ ...formData, email_enabled: e.target.checked })
                          }
                        />
                      }
                      label="Enable email notifications"
                    />
                    {formData.email_enabled && (
                      <TextField
                        fullWidth
                        size="small"
                        label="Email Address"
                        value={formData.email_address || ''}
                        onChange={(e) => setFormData({ ...formData, email_address: e.target.value })}
                        placeholder="Leave blank to use account email"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* SMS Notifications */}
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SmsIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="subtitle1">SMS Notifications</Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.sms_enabled}
                          onChange={(e) => setFormData({ ...formData, sms_enabled: e.target.checked })}
                        />
                      }
                      label="Enable SMS notifications"
                    />
                    {formData.sms_enabled && (
                      <TextField
                        fullWidth
                        size="small"
                        label="Phone Number"
                        value={formData.phone_number || ''}
                        onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                        placeholder="+1234567890"
                        sx={{ mt: 1 }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      SMS notifications require additional configuration
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Slack Notifications */}
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SlackIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="subtitle1">Slack Notifications</Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.slack_enabled}
                          onChange={(e) => setFormData({ ...formData, slack_enabled: e.target.checked })}
                        />
                      }
                      label="Enable Slack notifications"
                    />
                    {formData.slack_enabled && (
                      <TextField
                        fullWidth
                        size="small"
                        label="Slack Webhook URL"
                        value={formData.slack_webhook || ''}
                        onChange={(e) => setFormData({ ...formData, slack_webhook: e.target.value })}
                        placeholder="https://hooks.slack.com/..."
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Alert Type Preferences */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Alert Type Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure which alert types you want to receive
            </Typography>

            {alertTypes.map((alertType) => {
              const prefs = formData.alert_preferences?.[alertType] || { enabled: true, channels: ['in_app'] };
              return (
                <Box key={alertType} sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">{getAlertTypeLabel(alertType)}</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={prefs.enabled}
                          onChange={(e) =>
                            handleAlertPreferenceChange(alertType, 'enabled', e.target.checked)
                          }
                        />
                      }
                      label={prefs.enabled ? 'Enabled' : 'Disabled'}
                    />
                  </Box>
                  {prefs.enabled && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {['in_app', 'email', 'sms', 'slack'].map((channel) => {
                        const isSelected = prefs.channels?.includes(channel);
                        return (
                          <Chip
                            key={channel}
                            label={channel.replace('_', '-').toUpperCase()}
                            color={isSelected ? 'primary' : 'default'}
                            variant={isSelected ? 'filled' : 'outlined'}
                            onClick={() => {
                              const currentChannels = prefs.channels || [];
                              const newChannels = isSelected
                                ? currentChannels.filter((c: string) => c !== channel)
                                : [...currentChannels, channel];
                              handleAlertPreferenceChange(alertType, 'channels', newChannels);
                            }}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Paper>
        </Grid>

        {/* Quiet Hours */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quiet Hours
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Mute notifications during specific hours
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.quiet_hours_enabled}
                  onChange={(e) =>
                    setFormData({ ...formData, quiet_hours_enabled: e.target.checked })
                  }
                />
              }
              label="Enable quiet hours"
              sx={{ mb: 2 }}
            />

            {formData.quiet_hours_enabled && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Start Time"
                    value={formData.quiet_hours_start || '22:00'}
                    onChange={(e) => setFormData({ ...formData, quiet_hours_start: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="time"
                    label="End Time"
                    value={formData.quiet_hours_end || '08:00'}
                    onChange={(e) => setFormData({ ...formData, quiet_hours_end: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* Digest Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Digest Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Receive periodic summaries instead of real-time notifications
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.daily_digest_enabled}
                  onChange={(e) =>
                    setFormData({ ...formData, daily_digest_enabled: e.target.checked })
                  }
                />
              }
              label="Enable daily digest"
              sx={{ mb: 2 }}
            />

            {formData.daily_digest_enabled && (
              <TextField
                fullWidth
                type="time"
                label="Daily Digest Time"
                value={formData.daily_digest_time || '09:00'}
                onChange={(e) => setFormData({ ...formData, daily_digest_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Receive a daily summary at this time"
              />
            )}
          </Paper>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/alerts')}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              Save Preferences
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NotificationPreferences;
